import { useMemo, useRef, useState } from "react";
import { FormatterMode, resolveFormatterConfig } from "@textformatter/formatter";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import type {
	FormatterExample,
	FormatterMode as FormatterModeValue,
	FormatterModeProfiles,
	FormatterOptions,
	FormatterTransforms,
} from "./types";
import FormatterControls from "./FormatterControls";
import FormatterOutputPanel from "./FormatterOutputPanel";
import { createModeProfiles, createTransformProfiles, useFormattedHtml } from "./formatting";

type FormatterWorkspaceProps = {
	inputTitle?: string;
	showOptionDescriptions?: boolean;
	examples?: FormatterExample[];
	defaultInput?: string;
	defaultMode?: FormatterModeValue;
	defaultOptions?: Partial<FormatterOptions>;
	defaultOptionsByMode?: Partial<Record<FormatterModeValue, Partial<FormatterOptions>>>;
};

export default function FormatterWorkspace({
	inputTitle = "Input",
	showOptionDescriptions = true,
	examples = [],
	defaultInput = "",
	defaultMode = FormatterMode.Web,
	defaultOptions,
	defaultOptionsByMode,
}: FormatterWorkspaceProps) {
	const [input, setInput] = useState(defaultInput);
	const outputRef = useRef<HTMLDivElement | null>(null);
	const [mode, setMode] = useState<FormatterModeValue>(defaultMode);
	const [modeProfiles, setModeProfiles] = useState<FormatterModeProfiles>(() => {
		const profiles = createModeProfiles(defaultOptionsByMode);
		if (defaultOptions) {
			profiles[defaultMode] = resolveFormatterConfig({
				mode: defaultMode,
				options: { ...profiles[defaultMode], ...defaultOptions },
				fallbackMode: FormatterMode.Text,
			});
		}
		return profiles;
	});
	const [transformProfiles, setTransformProfiles] = useState<
		Record<FormatterModeValue, FormatterTransforms>
	>(() => createTransformProfiles());

	const activeOptions = useMemo(() => modeProfiles[mode], [mode, modeProfiles]);
	const activeTransforms = useMemo(() => transformProfiles[mode], [mode, transformProfiles]);
	const { output, format } = useFormattedHtml(input, mode, activeOptions, activeTransforms);

	function updateActiveOptions(next: FormatterOptions) {
		setModeProfiles((prev) => ({ ...prev, [mode]: next }));
	}

	function updateActiveTransforms(next: FormatterTransforms) {
		setTransformProfiles((prev) => ({ ...prev, [mode]: next }));
	}

	function handleFormatClick() {
		setInput(input);
		if (typeof window === "undefined" || window.innerWidth >= 1024) return;
		window.requestAnimationFrame(() => {
			outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	}

	return (
		<main className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 py-8 lg:grid-cols-2">
			<Card>
				<CardHeader>
					<CardTitle>{inputTitle}</CardTitle>
				</CardHeader>
				<CardContent>
					{examples.length ? (
						<div className="mb-4 flex flex-wrap gap-2">
							{examples.map((example) => (
								<Button
									key={example.id}
									variant="secondary"
									onClick={() => {
										setInput(example.text);
										const targetMode = example.contentType ?? mode;
										setMode(targetMode);
										if (example.options) {
											setModeProfiles((prev) => ({
												...prev,
												[targetMode]: resolveFormatterConfig({
													mode: targetMode,
													options: {
														...prev[targetMode],
														...example.options,
													},
													fallbackMode: FormatterMode.Text,
												}),
											}));
										}
										if (example.transforms) {
											setTransformProfiles((prev) => ({
												...prev,
												[targetMode]: {
													...prev[targetMode],
													...example.transforms,
												},
											}));
										}
									}}
								>
									{example.label}
								</Button>
							))}
						</div>
					) : null}

					<Textarea
						value={input}
						onChange={(e) => setInput(e.target.value)}
						placeholder="Paste raw text here..."
					/>

					<div className="mt-4 flex flex-wrap gap-2">
						<Button onClick={handleFormatClick}>Format</Button>
						<Button variant="ghost" onClick={() => setInput("")}>
							Clear
						</Button>
					</div>

					<FormatterControls
						mode={mode}
						onModeChange={setMode}
						options={activeOptions}
						onChange={updateActiveOptions}
						transforms={activeTransforms}
						onTransformsChange={updateActiveTransforms}
						showOptionDescriptions={showOptionDescriptions}
					/>
				</CardContent>
			</Card>
			<div ref={outputRef}>
				<FormatterOutputPanel output={output} format={format} />
			</div>
		</main>
	);
}
